import { useEffect, useState, useCallback, useRef } from "react";
import { RenderHubCard } from "../renderhub/RenderHubCard";
import { toast } from "sonner";
import ModuleFlowModal from "./ModuleFlowModal";
import InvoiceOwnerModal from "./InvoiceOwnerModal";
import SendProposalModal from "./SendProposalModal";
import BankTrackerModal from "./BankTrackerModal";
import DoctrineToolsModal from "./DoctrineToolsModal";
import SoftLaunchReadiness from "./SoftLaunchReadiness";
import VibeBoard from "./VibeBoard";
import VendorHunterModal from "./VendorHunterModal";
import CountyRegistryModal from "./CountyRegistryModal";
import MediaLicenseModal from "./MediaLicenseModal";
import LiveStatusHealthModal from "./LiveStatusHealthModal";
import PhoneDialerModal from "./PhoneDialerModal";
import SendSMSModal from "./SendSMSModal";
import WaterFiltrationModal from "./WaterFiltrationModal";
import WhatsNewBanner from "./WhatsNewBanner";
import CommunityDispatchPanel from "./CommunityDispatchPanel";
import PropertyPhotoRender from "./PropertyPhotoRender";
import CockpitHelpPanel from "./CockpitHelpPanel";
import LandlordTriggers from "./LandlordTriggers";
import LiveLedgerViewer from "./LiveLedgerViewer";
import CEOSanctuary from "./CEOSanctuary";
import ScopeLockRender from "./ScopeLockRender";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_LABELS = {
  always_approve: { label: "Always Approve", color: "#FFB347", emoji: "🛂" },
  auto_send: { label: "Auto-Send", color: "#00DC78", emoji: "🤖" },
  off: { label: "Off", color: "#9CA3AF", emoji: "⏸️" },
};

const STATUS_BADGE = {
  active: { label: "RUNNING", color: "#00DC78" },
  auto_send: { label: "AUTO-SEND", color: "#00DC78" },
  always_approve: { label: "AWAITING YES", color: "#FFB347" },
  off: { label: "OFF", color: "#9CA3AF" },
  paused: { label: "PAUSED", color: "#F87171" },
};

const Cockpit = ({ setTab, dashboardRole, currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [categories, setCategories] = useState({});
  const [showAuto, setShowAuto] = useState(false);
  const [health, setHealth] = useState(null);
  const [showHealth, setShowHealth] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [invoiceOwnerOpen, setInvoiceOwnerOpen] = useState(false);
  const [sendProposalOpen, setSendProposalOpen] = useState(false);
  const [bankTrackerOpen, setBankTrackerOpen] = useState(false);
  const [doctrineOpen, setDoctrineOpen] = useState(false);
  const [vendorHunterOpen, setVendorHunterOpen] = useState(false);
  const [countyRegistryOpen, setCountyRegistryOpen] = useState(false);
  const [mediaLicenseOpen, setMediaLicenseOpen]     = useState(false);
  const [liveStatusOpen, setLiveStatusOpen]         = useState(false);
  const [phoneDialerOpen, setPhoneDialerOpen]       = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [waterOpen, setWaterOpen] = useState(false);
  const [photoRenderOpen, setPhotoRenderOpen] = useState(false);

  // ─── CEO Sanctuary mode (Feb 12, 2026, late night) ────────────────
  // Shannon's directive: "A busy dashboard does nothing for me but
  // give me anxiety. My business needs to be pushed completely
  // streamlined on spiritual peace, no friction, and fair. Job
  // creation and building community."
  //
  // The Cockpit now opens in Sanctuary mode by default — one greeting,
  // one focus tile, one primary CTA. She taps "Show operational
  // detail" to unlock the full busy surface.
  const [sanctuaryMode, setSanctuaryMode] = useState(() => {
    try {
      // Remember her last choice across sessions so she doesn't have
      // to re-toggle every visit.
      const stored = sessionStorage.getItem("mf_cockpit_sanctuary");
      return stored === null ? true : stored === "true";
    } catch { return true; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("mf_cockpit_sanctuary", String(sanctuaryMode)); } catch { /* ignore */ }
  }, [sanctuaryMode]);

  // ─── Camera / Snap Photo wiring (Feb 12, 2026) ────────────────────
  // Uses the SAME native camera pattern already deployed in FieldMode
  // (input type=file with capture="environment"). No new component,
  // no shift-gate — booth visitor taps the button, phone opens rear
  // camera, capture uploads to the existing field intake endpoint,
  // AND immediately triggers the auto-pipeline (spatial parse →
  // 2D floorplan → variation → live-inventory tally).
  const cockpitCameraRef = useRef(null);
  const [uploadingCameraShot, setUploadingCameraShot] = useState(false);
  const [autoPipelineResult, setAutoPipelineResult] = useState(null);
  const [scopeClass, setScopeClass] = useState("interior");  // interior | exterior

  const openCockpitCamera = () => {
    if (cockpitCameraRef.current) cockpitCameraRef.current.click();
  };

  const onCockpitPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCameraShot(true);
    setAutoPipelineResult(null);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => {
          const s = r.result || "";
          const comma = s.indexOf(",");
          res(comma >= 0 ? s.slice(comma + 1) : s);
        };
        r.onerror = () => rej(r.error);
        r.readAsDataURL(file);
      });

      // Step 1: upload the photo to the intake endpoint.
      const uploadResp = await fetch(`${BACKEND_URL}/api/admin/cockpit/photo`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_b64: b64,
          caption: `Cockpit capture · ${new Date().toLocaleString()}`,
        }),
      });
      let photoId = "";
      if (uploadResp.ok) {
        const d = await uploadResp.json();
        photoId = d.photo_id || "";
        toast.success("✓ Photo captured. Running auto-pipeline…");
      } else {
        toast.warning("Photo capture queued for sync — running auto-pipeline anyway.");
      }

      // Step 2: fire the auto-pipeline. Spatial parse → floorplan →
      // top-tier variation → live-inventory tally. Zero prompts.
      const lastAddress = (() => { try { return sessionStorage.getItem("mf_last_address") || ""; } catch { return ""; } })();
      const pipeResp = await fetch(`${BACKEND_URL}/api/field/photo/auto-pipeline`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photo_id:    photoId,
          address:     lastAddress,
          room_hint:   "kitchen",
          unit_type:   "2bed",
          scope_class: scopeClass,
        }),
      });
      if (pipeResp.ok) {
        const d = await pipeResp.json();
        setAutoPipelineResult(d);
        toast.success(`✓ Auto-pipeline complete · ${d.spatial_parse?.width_ft}′ × ${d.spatial_parse?.length_ft}′ · $${(d.line_item_total || 0).toLocaleString()}`);
      } else {
        toast.error("Auto-pipeline could not run. Photo saved.");
      }
    } catch (err) {
      toast.error("Photo capture failed. Try again.");
    } finally {
      setUploadingCameraShot(false);
      if (cockpitCameraRef.current) cockpitCameraRef.current.value = "";
    }
  };

  const openNspireWalkthrough = () => {
    // Route to the existing NspireWalkthrough (already mounted inside
    // Design Studio → Compliance → NSPIRE tab). Query param triggers
    // auto-tab-select on load so the visitor lands directly on the
    // walkthrough with zero extra taps.
    window.location.href = "/design-studio?tab=nspire";
  };

  const firstName = (currentUser?.name || currentUser?.email || "there").split(/[\s@]/)[0];

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/cockpit`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      console.warn("[Cockpit] aggregator load failed:", e);
      setErr("Could not load Cockpit data. Try Refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/cockpit/categories`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setCategories(d.categories || {});
      }
    } catch (e) { console.warn("[Cockpit] categories load failed:", e); }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/system-health`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setHealth(d);
      }
    } catch (e) { console.warn("[Cockpit] health load failed:", e); }
  }, []);

  useEffect(() => { load(); loadCategories(); loadHealth(); }, [load, loadCategories, loadHealth]);
  useEffect(() => {
    const t = setInterval(load, 180000);
    const t2 = setInterval(loadHealth, 600000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, [load, loadHealth]);

  const updateCategory = async (catKey, newMode) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/cockpit/categories`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: catKey, mode: newMode }),
      });
      if (r.ok) {
        setCategories((prev) => ({
          ...prev,
          [catKey]: { ...prev[catKey], mode: newMode },
        }));
        load();
      }
    } catch { /* silent */ }
  };

  const goTo = (tabKey) => {
    if (!tabKey || !setTab) return;
    setTab(tabKey);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
  };

  if (loading) {
    return (
      <div data-testid="cockpit-loading" style={{ padding: "3rem 2rem", textAlign: "center", color: "#9CA3AF" }}>
        Loading your Cockpit…
      </div>
    );
  }

  const sum = data?.summary || {};

  return (
    <div data-testid="cockpit" style={{ display: "grid", gap: "1.4rem", paddingBottom: "3rem" }}>
      {/* CEO Sanctuary — the calm-first view. When Shannon is in
          sanctuary mode she sees ONLY: greeting → top trigger →
          ethos strip → escape hatch. Every other Cockpit panel
          collapses. Sanctuary mode is default and remembered per
          session. */}
      {sanctuaryMode && (
        <CEOSanctuary onEnterOperational={() => setSanctuaryMode(false)} />
      )}

      {/* Small pill for returning to sanctuary once in operational mode */}
      {!sanctuaryMode && (
        <button
          data-testid="cockpit-return-to-sanctuary"
          onClick={() => setSanctuaryMode(true)}
          style={{
            justifySelf: "flex-start",
            background: "transparent",
            border: "1px dashed rgba(90,200,216,0.4)",
            color: "#5AC8D8",
            padding: "5px 12px", borderRadius: 999,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700,
            cursor: "pointer",
          }}>
          ← Return to sanctuary
        </button>
      )}

      {/* SCOPE LOCK · INLINE RENDER — appears the moment a variation is
          selected in Design Studio. Mounted here so it survives tab
          switches (sessionStorage-backed) and displays in both sanctuary
          and operational modes. Zero-friction hands-free rendering per
          master pipeline directive Feb 12, 2026. */}
      <ScopeLockRender />

      {/* Hidden camera input — triggered by the "📷 Snap Photo" button
          in the action row. Uses the same native capture pattern that
          FieldMode already uses (line 891 of FieldMode.js). No new
          component; wiring only. */}
      <input
        ref={cockpitCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onCockpitPhotoSelected}
        data-testid="cockpit-camera-input"
        style={{ display: "none" }}
      />

      {/* Auto-Pipeline result — appears immediately after Snap Photo.
          Shows the spatial parse, 2D floorplan preview, top variation,
          and live-inventory line-item tally. No manual prompts. */}
      {autoPipelineResult && (
        <div data-testid="cockpit-auto-pipeline-result" style={{
          background: "rgba(0,220,120,0.06)",
          border: "1px solid rgba(0,220,120,0.35)",
          borderRadius: 12,
          padding: "14px 16px 16px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800 }}>
                &gt; AUTO-PIPELINE COMPLETE · HANDS-FREE
              </div>
              <div style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, marginTop: 3 }}>
                {autoPipelineResult.spatial_parse.width_ft}′ × {autoPipelineResult.spatial_parse.length_ft}′ · {autoPipelineResult.spatial_parse.floor_sqft} sqft
              </div>
              {autoPipelineResult.scope_class && (
                <div style={{
                  display: "inline-block", marginTop: 6,
                  padding: "3px 8px", borderRadius: 3,
                  background: autoPipelineResult.scope_class === "interior" ? "rgba(0,220,120,0.14)" : "rgba(255,179,71,0.14)",
                  border: `1px solid ${autoPipelineResult.scope_class === "interior" ? "#00DC78" : "#FFB347"}`,
                  color: autoPipelineResult.scope_class === "interior" ? "#00DC78" : "#FFB347",
                  fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem",
                  letterSpacing: "0.14em", fontWeight: 800,
                }}>
                  {autoPipelineResult.scope_class === "interior" ? "◈ INTERIOR · CORE" : "◇ EXTERIOR · LANDLORD ADD-ON"}
                </div>
              )}
              {autoPipelineResult.scope_note && (
                <div style={{ color: "#B4BEC8", fontSize: "0.7rem", marginTop: 4, fontStyle: "italic", lineHeight: 1.45 }}>
                  {autoPipelineResult.scope_note}
                </div>
              )}
            </div>
            <button onClick={() => setAutoPipelineResult(null)}
                    data-testid="close-auto-pipeline"
                    style={{ background: "transparent", color: "#7E92A8", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, padding: "3px 8px", fontSize: "0.7rem", cursor: "pointer" }}>
              close
            </button>
          </div>

          {/* Spatial parse strip */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 12 }}>
            {[
              ["CEILING",  `${autoPipelineResult.spatial_parse.ceiling_ft}′`],
              ["DOORS",    autoPipelineResult.spatial_parse.doors],
              ["WINDOWS",  autoPipelineResult.spatial_parse.windows],
              ["WALL SQFT", autoPipelineResult.spatial_parse.wall_sqft],
            ].map(([k, v]) => (
              <div key={k} style={{ padding: "8px 10px", background: "rgba(0,0,0,0.28)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6 }}>
                <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.14em", fontWeight: 800 }}>{k}</div>
                <div style={{ color: "#fff", fontFamily: "'JetBrains Mono', monospace", fontSize: "1rem", fontWeight: 800, marginTop: 2 }}>{v}</div>
              </div>
            ))}
          </div>

          {/* 2D floorplan preview */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 12 }}>
            <svg width="180" height="140" viewBox="0 0 200 130" style={{ flex: "0 0 auto" }} data-testid="auto-pipeline-floorplan">
              <rect x="30" y="15" width="140" height="100" fill="rgba(0,255,255,0.10)" stroke="#00FFFF" strokeWidth="1.5" rx="2" />
              {(autoPipelineResult.floorplan_2d.door_positions || []).map((d, i) => (
                <line key={i} x1={30 + 140 * (d.offset_pct / 100)} y1="15" x2={30 + 140 * (d.offset_pct / 100) + 15} y2="15" stroke="#FFB347" strokeWidth="2.5" />
              ))}
              {(autoPipelineResult.floorplan_2d.window_positions || []).map((w, i) => (
                <line key={i} x1={30 + 140 * (w.offset_pct / 100)} y1="115" x2={30 + 140 * (w.offset_pct / 100) + 20} y2="115" stroke="#5AC8D8" strokeWidth="2" />
              ))}
            </svg>
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.16em", fontWeight: 800, marginBottom: 6 }}>
                AUTO-SELECTED VARIATION
              </div>
              <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800 }}>
                {autoPipelineResult.auto_variation.stream_label}
              </div>
              <div style={{ color: "#B4BEC8", fontSize: "0.75rem", marginTop: 3, lineHeight: 1.5 }}>
                {autoPipelineResult.auto_variation.tone}
              </div>
              <div style={{ color: "#FFB347", fontSize: "0.65rem", marginTop: 6, fontStyle: "italic", lineHeight: 1.4 }}>
                {autoPipelineResult.acclamation_clause}
              </div>
            </div>
          </div>

          {/* Live inventory line-item tally */}
          <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.16em", fontWeight: 800, marginBottom: 6 }}>
            LIVE INVENTORY · LINE-ITEM TALLY
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(autoPipelineResult.live_inventory || []).map(l => (
              <div key={l.category} data-testid={`auto-pipeline-line-${l.category}`} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "6px 10px", background: "rgba(0,0,0,0.25)", borderRadius: 4,
                fontSize: "0.75rem",
              }}>
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <span style={{ color: "#fff", fontWeight: 700 }}>{l.category}</span>
                  <span style={{ color: "#7E92A8", marginLeft: 6 }}>· {l.vendor}</span>
                </div>
                <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, flex: "0 0 auto", marginLeft: 8 }}>
                  {l.quantity}× · ${l.subtotal.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 10, padding: "8px 10px", background: "rgba(0,220,120,0.12)", border: "1px solid rgba(0,220,120,0.4)", borderRadius: 6 }}>
            <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.16em", fontWeight: 800 }}>
              TOTAL · LIVE INVENTORY
            </div>
            <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "1.1rem", fontWeight: 800 }} data-testid="auto-pipeline-total">
              ${(autoPipelineResult.line_item_total || 0).toLocaleString()}
            </div>
          </div>
        </div>
      )}
      {/* HEADER + KPI strip */}
      {!sanctuaryMode && (
      <div style={{
        background: "linear-gradient(135deg, #0a1929 0%, #1b2a4e 100%)",
        border: "1px solid rgba(184,145,90,0.4)",
        borderRadius: 10,
        padding: "1.4rem 1.6rem",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "0.7rem" }}>
          <div>
            <div style={{ fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#B8915A", fontWeight: 800 }}>
              Cockpit · Your Command Center
            </div>
            <h1 data-testid="cockpit-title" style={{ color: "#F5EFE1", fontSize: "1.7rem", margin: "0.3rem 0 0", fontWeight: 700 }}>
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, {firstName}.
            </h1>
            <div style={{ color: "#ADBED1", fontSize: "0.9rem", marginTop: 4 }}>
              {sum.needs_approval_count > 0
                ? <><strong style={{ color: "#FFB347" }}>{sum.needs_approval_count}</strong> waiting on your YES · </>
                : "Nothing waiting on you · "}
              <strong style={{ color: "#00DC78" }}>{sum.running_on_auto_count}</strong> running on auto
              {sum.needs_attention_count > 0 && <> · <strong style={{ color: "#F87171" }}>{sum.needs_attention_count}</strong> need your eyes</>}
            </div>
          </div>
          <RenderHubCard />
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", width: "100%", justifyContent: "flex-start" }} data-testid="cockpit-actions-row">
            <button data-testid="cockpit-snap-photo" type="button" onClick={openCockpitCamera} disabled={uploadingCameraShot}
                    style={{ background: "linear-gradient(90deg, #5AC8D8, #3AA8B8)", color: "#03151F", border: "none", padding: "0.5rem 0.95rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>
              📷 {uploadingCameraShot ? "Uploading…" : "Snap Photo"}
            </button>
            <button data-testid="cockpit-scope-toggle" type="button"
                    onClick={() => setScopeClass(s => s === "interior" ? "exterior" : "interior")}
                    style={{ background: scopeClass === "interior" ? "#0A3A2E" : "#3A2A0A",
                             color: scopeClass === "interior" ? "#00DC78" : "#FFB347",
                             border: `1px solid ${scopeClass === "interior" ? "#00DC78" : "#FFB347"}`,
                             padding: "0.4rem 0.75rem", borderRadius: 4, fontSize: "0.68rem",
                             fontWeight: 800, cursor: "pointer", letterSpacing: "0.08em" }}>
              {scopeClass === "interior" ? "◈ INTERIOR · CORE" : "◇ EXTERIOR · ADD-ON"}
            </button>
            <button data-testid="cockpit-nspire-walkthrough" type="button" onClick={openNspireWalkthrough}
                    style={{ background: "linear-gradient(90deg, #00DC78, #00B364)", color: "#03151F", border: "none", padding: "0.5rem 0.95rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>
              🛡️ NSPIRE Walkthrough
            </button>
            <button data-testid="cockpit-crew-mobile" type="button" onClick={() => (window.location.href = "/mobile")}
                    style={{ background: "linear-gradient(90deg, #A78BFA, #7C6AE3)", color: "#03151F", border: "none", padding: "0.5rem 0.95rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>
              📱 Crew Blueprint
            </button>
            <button data-testid="cockpit-expo-live" type="button" onClick={() => (window.location.href = "/expo")}
                    style={{ background: "linear-gradient(90deg, #FF4D6D, #FFB347)", color: "#03151F", border: "none", padding: "0.5rem 0.95rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>
              🎪 Expo Live
            </button>
            <button data-testid="cockpit-send-proposal" type="button" onClick={() => setSendProposalOpen(true)}
                    style={{ background: "linear-gradient(90deg, #5AC8D8, #3AA8B8)", color: "#062617", border: "none", padding: "0.5rem 0.95rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>
              🎨 Send Proposal
            </button>
            <button data-testid="cockpit-invoice-owner" type="button" onClick={() => setInvoiceOwnerOpen(true)}
                    style={{ background: "linear-gradient(90deg, #00DC78, #00B364)", color: "#062617", border: "none", padding: "0.5rem 0.95rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 800, cursor: "pointer" }}>
              💰 Invoice Owner
            </button>
            <button data-testid="cockpit-bank-tracker" type="button" onClick={() => setBankTrackerOpen(true)}
                    style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A", padding: "0.5rem 0.9rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 700, cursor: "pointer" }}>
              🏦 Banks
            </button>
            <button data-testid="cockpit-doctrine" type="button" onClick={() => setDoctrineOpen(true)}
                    style={{ background: "transparent", color: "#E8B8C4", border: "1px solid #E8B8C4", padding: "0.5rem 0.9rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 700, cursor: "pointer" }}>
              📋 Doctrine
            </button>
            <button data-testid="cockpit-refresh" type="button" onClick={load}
                    style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A", padding: "0.5rem 0.9rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 700, cursor: "pointer" }}>
              ↻ Refresh
            </button>
            <button data-testid="cockpit-settings-toggle" type="button" onClick={() => setSettingsOpen(!settingsOpen)}
                    style={{ background: settingsOpen ? "#B8915A" : "transparent", color: settingsOpen ? "#1b2a4e" : "#B8915A", border: "1px solid #B8915A", padding: "0.5rem 0.9rem", borderRadius: 4, fontSize: "0.82rem", fontWeight: 700, cursor: "pointer" }}>
              ⚙️ Auto-Pilot
            </button>
          </div>
        </div>
      </div>
      )}

      {!sanctuaryMode && data?.sandbox_mode && (
        <div data-testid="cockpit-sandbox-note" style={{ background: "rgba(255,179,71,0.12)", border: "1px solid rgba(255,179,71,0.4)", borderRadius: 6, padding: "0.7rem 1rem", color: "#FFD699", fontSize: "0.85rem" }}>
          🟡 <strong>Sandbox mode is ON</strong> — every outbound is rerouting to nivoomforward@gmail.com so you can audit before flipping live.
        </div>
      )}

      {!sanctuaryMode && err && (
        <div data-testid="cockpit-error" style={{ background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.4)", color: "#FCA5A5", padding: "0.7rem 1rem", borderRadius: 6 }}>
          ⚠ {err}
        </div>
      )}

      {/* ZERO-DOLLAR ADMIN TOOLS — Vendor Hunter · County Registry · Media License */}
      {!sanctuaryMode && (dashboardRole === "admin" || dashboardRole === "ceo" || !dashboardRole) && (
        <div data-testid="cockpit-zero-dollar-tools" style={{
          background: "linear-gradient(90deg, rgba(184,145,90,0.10) 0%, rgba(0,220,120,0.08) 100%)",
          border: "1px solid rgba(184,145,90,0.35)", borderRadius: 10,
          padding: "0.85rem 1.1rem", display: "flex", flexWrap: "wrap",
          gap: "0.5rem", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ color: "#B8915A", fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase" }}>
              🛡 Zero-Dollar Admin · R12
            </div>
            <div style={{ color: "#F3F4F6", fontSize: "0.82rem", marginTop: 3 }}>
              Live Status · Vendor Hunter · County GIS · Owner Media License. Zero paid keys.
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            <button type="button" data-testid="ctrl-live-status" onClick={() => setLiveStatusOpen(true)}
                    style={ctaBtn("linear-gradient(90deg,#00DC78,#5AC8D8)", "#062617", "#00DC78")}>
              🛡 Live Status
            </button>
            <button type="button" data-testid="ctrl-phone-dialer" onClick={() => setPhoneDialerOpen(true)}
                    style={ctaBtn("linear-gradient(90deg,#B8915A,#5AC8D8)", "#0a1929", "#B8915A")}>
              ☎ Dialer
            </button>
            <button type="button" data-testid="ctrl-vendor-hunter" onClick={() => setVendorHunterOpen(true)}
                    style={ctaBtn("#B8915A", "#0a1929", "#B8915A")}>
              🎯 Vendor Hunter
            </button>
            <button type="button" data-testid="ctrl-county-registry" onClick={() => setCountyRegistryOpen(true)}
                    style={ctaBtn("transparent", "#5AC8D8", "#5AC8D8")}>
              🗺 County Registry
            </button>
            <button type="button" data-testid="ctrl-media-license" onClick={() => setMediaLicenseOpen(true)}
                    style={ctaBtn("transparent", "#00DC78", "#00DC78")}>
              📄 Media License
            </button>
          </div>
        </div>
      )}

      {/* SETTINGS DRAWER (collapsible) */}
      {!sanctuaryMode && settingsOpen && (
        <div data-testid="cockpit-settings" style={{
          background: "rgba(15,23,42,0.7)", border: "1px solid rgba(184,145,90,0.4)",
          borderRadius: 8, padding: "1.2rem 1.4rem",
        }}>
          <div style={{ color: "#B8915A", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>
            🛂 Per-Category Approval Gates
          </div>
          <div style={{ color: "#ADBED1", fontSize: "0.86rem", marginBottom: "0.9rem" }}>
            Decide per category: should it ask before sending, send on its own, or stay off?
          </div>
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {Object.entries(categories).map(([catKey, meta]) => (
              <div key={catKey} data-testid={`cat-row-${catKey}`}
                   style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.7rem", alignItems: "center", padding: "0.6rem 0.8rem", background: "rgba(255,255,255,0.03)", borderRadius: 5, border: "1px solid rgba(255,255,255,0.05)" }}>
                <div>
                  <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.9rem" }}>{meta.label}</div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.78rem", marginTop: 2 }}>{meta.desc}</div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {["always_approve", "auto_send", "off"].map((m) => {
                    const active = meta.mode === m;
                    const cfg = MODE_LABELS[m];
                    return (
                      <button key={m} type="button" onClick={() => updateCategory(catKey, m)}
                              data-testid={`cat-${catKey}-${m}`}
                              style={{
                                background: active ? cfg.color : "transparent",
                                color: active ? "#1b2a4e" : cfg.color,
                                border: `1px solid ${cfg.color}`,
                                padding: "0.3rem 0.7rem", borderRadius: 4,
                                fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}>
                        {cfg.emoji} {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SYSTEM NERVOUS SYSTEM strip — always visible so Shannon knows the platform is alive */}
      {!sanctuaryMode && health && (
        <div data-testid="cockpit-nervous-system" style={{
          background: health.overall === "green" ? "rgba(0,220,120,0.08)"
                    : health.overall === "yellow" ? "rgba(255,179,71,0.12)"
                    : "rgba(220,38,38,0.14)",
          border: `1px solid ${health.overall === "green" ? "rgba(0,220,120,0.4)"
                              : health.overall === "yellow" ? "rgba(255,179,71,0.45)"
                              : "rgba(220,38,38,0.5)"}`,
          borderRadius: 8, padding: "0.7rem 1rem",
        }}>
          <div onClick={() => setShowHealth(!showHealth)}
               style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap" }}>
              <span data-testid="nervous-system-dot" style={{
                width: 10, height: 10, borderRadius: "50%",
                background: health.overall === "green" ? "#00DC78"
                         : health.overall === "yellow" ? "#FFB347" : "#F87171",
                boxShadow: `0 0 8px ${health.overall === "green" ? "#00DC78" : health.overall === "yellow" ? "#FFB347" : "#F87171"}`,
              }} />
              <div>
                <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.88rem" }}>
                  🩺 System Nervous System — {health.counts?.green || 0}/{health.counts?.total || 0} green
                </div>
                <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginTop: 2 }}>
                  {health.overall_msg}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexShrink: 0 }}>
              <button type="button" data-testid="see-the-flow"
                      onClick={(e) => { e.stopPropagation(); setFlowOpen(true); }}
                      style={{ background: "transparent", border: "1px solid rgba(184,145,90,0.5)",
                               color: "#B8915A", padding: "0.3rem 0.75rem", borderRadius: 4,
                               fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
                               whiteSpace: "nowrap" }}>
                🕸️ See the flow
              </button>
              <button type="button" data-testid="nervous-system-toggle"
                      onClick={(e) => { e.stopPropagation(); setShowHealth(!showHealth); }}
                      style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)",
                               color: "#ADBED1", padding: "0.3rem 0.75rem", borderRadius: 4,
                               fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
                               whiteSpace: "nowrap" }}>
                {showHealth ? "Hide" : `Show all ${health.counts?.total || 10}`}
              </button>
            </div>
          </div>
          {showHealth && (
            <div style={{ marginTop: "0.8rem", display: "grid", gap: "0.4rem" }}>
              {(health.checks || []).map((c, idx) => {
                const dotColor = c.status === "green" ? "#00DC78"
                              : c.status === "yellow" ? "#FFB347" : "#F87171";
                return (
                  <div key={c.key} data-testid={`nervous-check-${c.key}`}
                       style={{ display: "flex", gap: "0.7rem", alignItems: "center",
                                background: "rgba(0,0,0,0.15)", padding: "0.45rem 0.7rem",
                                borderRadius: 4, borderLeft: `3px solid ${dotColor}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#F3F4F6", fontWeight: 600, fontSize: "0.82rem" }}>{c.label}</div>
                      <div style={{ color: "#9CA3AF", fontSize: "0.72rem", marginTop: 1 }}>{c.detail}</div>
                    </div>
                  </div>
                );
              })}
              <div style={{ color: "#6B7280", fontSize: "0.7rem", textAlign: "right", marginTop: 4 }}>
                Auto-refreshes every 2 min · background worker heartbeats every 5 min · last: {health.generated_at ? new Date(health.generated_at).toLocaleTimeString() : "—"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Autonomous COO — Soft-Launch Readiness (fail-quiet if endpoint down) */}
      {!sanctuaryMode && <SoftLaunchReadiness />}

      {/* Landlord Automated Triggers — hands-free compliance clock feed */}
      {!sanctuaryMode && <LandlordTriggers />}

      {/* Immutable Audit Ledger — live tail with server-verified hash chain */}
      {!sanctuaryMode && <LiveLedgerViewer />}

      {/* What's new since last deploy */}
      {!sanctuaryMode && <WhatsNewBanner />}

      {/* Vibe Board — live activity stream, auto-refresh every 10s */}
      {!sanctuaryMode && <VibeBoard />}

      {/* Community Dispatch — top-3 qualified solo workers, one-tap dispatch */}
      {!sanctuaryMode && <CommunityDispatchPanel />}

      {/* PANEL 1: NEEDS YOUR YES (biggest, top) */}
      {!sanctuaryMode && (
      <Panel
        testId="cockpit-needs-approval"
        emoji="⏳"
        title="Needs Your YES"
        subtitle={(sum.needs_approval_count ?? 0) === 0 ? "Inbox zero. The system is humming." : `${sum.needs_approval_count} outbound${sum.needs_approval_count === 1 ? "" : "s"} waiting on your one-tap call.`}
        accent="#FFB347"
        empty={sum.needs_approval_count === 0}
        emptyMsg="🟢 Nothing waiting. Take a breath."
        items={data?.needs_approval || []}
        renderItem={(item, idx) => (
          <div key={idx} data-testid={`needs-yes-item-${idx}`} style={itemRow}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.95rem" }}>{item.title}</div>
              <div style={{ color: "#ADBED1", fontSize: "0.82rem", marginTop: 3 }}>{item.subtitle}</div>
              {item.action_hint && (
                <div style={{ color: "#FFB347", fontSize: "0.76rem", marginTop: 4, fontStyle: "italic" }}>→ {item.action_hint}</div>
              )}
            </div>
            {item.kind === "owner_proposal_submitted" ? (
              <button
                type="button"
                data-testid={`needs-yes-convert-${idx}`}
                onClick={async () => {
                  try {
                    const r = await fetch(`${BACKEND_URL}/api/admin/proposals/${item.id}/convert`, {
                      method: "POST", credentials: "include",
                    });
                    const d = await r.json();
                    if (r.ok) {
                      alert(`✓ Converted to Memory Book: ${d.entry?.title || 'entry'}`);
                      load();
                    } else {
                      alert(`Could not convert: ${d.detail || 'unknown error'}`);
                    }
                  } catch { alert("Network error."); }
                }}
                style={ctaBtn("#A78BFA", "#1b2a4e")}
              >Convert →</button>
            ) : (
              <button type="button" onClick={() => goTo(item.deep_link)}
                      data-testid={`needs-yes-decide-${idx}`}
                      style={ctaBtn("#FFB347", "#1b2a4e")}>
              Open →
              </button>
            )}
          </div>
        )}
      />
      )}

      {/* PANEL 3: NEEDS YOUR EYES (escalations) */}
      {!sanctuaryMode && (data?.needs_attention || []).length > 0 && (
        <Panel
          testId="cockpit-needs-attention"
          emoji="🔴"
          title="Needs Your Eyes"
          subtitle="Field triggers, bounces, or crashes from the last 24 hours."
          accent="#F87171"
          items={data.needs_attention}
          renderItem={(item, idx) => (
            <div key={idx} data-testid={`needs-eyes-item-${idx}`} style={itemRow}>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.93rem" }}>{item.title}</div>
                <div style={{ color: "#ADBED1", fontSize: "0.82rem", marginTop: 3 }}>{item.subtitle}</div>
              </div>
              <button type="button" onClick={() => goTo(item.deep_link)}
                      data-testid={`needs-eyes-open-${idx}`}
                      style={ctaBtn("#F87171", "#1b2a4e")}>
                Triage →
              </button>
            </div>
          )}
        />
      )}

      {/* PANEL 2: RUNNING ON AUTO (collapsible — default collapsed for clarity) */}
      {!sanctuaryMode && (
      <div data-testid="cockpit-running-auto" style={panelOuter}>
        <div onClick={() => setShowAuto(!showAuto)}
             style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", padding: "1.1rem 1.3rem" }}>
          <div>
            <div style={panelBadge("#00DC78")}>🟢 RUNNING ON AUTO</div>
            <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1.05rem", marginTop: 4 }}>
              {sum.running_on_auto_count} workflows working for you right now
            </div>
            <div style={{ color: "#9CA3AF", fontSize: "0.82rem", marginTop: 2 }}>
              Click to expand · adjust gates in ⚙️ Settings above.
            </div>
          </div>
          <button type="button" data-testid="cockpit-auto-toggle"
                  style={{ background: "transparent", border: "1px solid #00DC78", color: "#00DC78", padding: "0.35rem 0.8rem", borderRadius: 4, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
            {showAuto ? "Hide" : "Show"}
          </button>
        </div>
        {showAuto && (
          <div style={{ padding: "0 1.3rem 1.1rem", display: "grid", gap: "0.5rem" }}>
            {(data?.running_on_auto || []).map((r, idx) => {
              const badge = STATUS_BADGE[r.status] || { label: (r.status || "").toUpperCase() || "UNKNOWN", color: "#9CA3AF" };
              return (
                <div key={idx} data-testid={`auto-item-${idx}`} style={itemRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.9rem" }}>
                      {r.label}{" "}
                      <span style={{ fontSize: "0.7rem", padding: "0.12rem 0.5rem", borderRadius: 3, marginLeft: 6, background: `${badge.color}22`, color: badge.color, fontWeight: 800, letterSpacing: "0.06em" }}>
                        {badge.label}
                      </span>
                    </div>
                    <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginTop: 2 }}>{r.desc}</div>
                  </div>
                  <button type="button" onClick={() => goTo(r.deep_link)}
                          data-testid={`auto-open-${idx}`}
                          style={ctaBtn("transparent", "#00DC78", "#00DC78")}>
                    Open →
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* PANEL 4: TODAY'S WINS (small, bottom) */}
      {!sanctuaryMode && (
      <Panel
        testId="cockpit-todays-wins"
        emoji="📈"
        title="Today's Wins"
        subtitle="Opens, clicks, opt-ins from the last 24 hours."
        accent="#A78BFA"
        items={(data?.todays_wins || []).slice(0, 6)}
        empty={(data?.todays_wins || []).length === 0}
        emptyMsg="Quiet so far. Wins land here as people open + click."
        renderItem={(item, idx) => (
          <div key={idx} data-testid={`win-item-${idx}`} style={{ ...itemRow, padding: "0.6rem 0.9rem" }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#F3F4F6", fontWeight: 600, fontSize: "0.88rem" }}>{item.title}</div>
              <div style={{ color: "#9CA3AF", fontSize: "0.74rem", marginTop: 1 }}>{item.subtitle}</div>
            </div>
            <div style={{ color: "#9CA3AF", fontSize: "0.7rem", whiteSpace: "nowrap" }}>
              {item.ts ? new Date(item.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
            </div>
          </div>
        )}
      />
      )}

      {!sanctuaryMode && (
      <div style={{ textAlign: "center", color: "#6B7280", fontSize: "0.78rem", marginTop: "0.5rem" }}>
        Refreshes every 60 seconds · Last sync: {data?.as_of ? new Date(data.as_of).toLocaleTimeString() : "—"}
      </div>
      )}

      {/* QUICK DOCS — copy-paste / download anytime */}
      {!sanctuaryMode && (
      <div data-testid="cockpit-quick-docs" style={panelOuter}>
        <div style={{ padding: "1.1rem 1.3rem 0.6rem", borderLeft: "4px solid #B8915A" }}>
          <div style={panelBadge("#B8915A")}>📋 Quick Docs · Copy-Paste / Download</div>
          <div style={{ color: "#ADBED1", fontSize: "0.85rem", marginTop: 4 }}>
            Bank applications, grants, underwriters — grab the canonical paperwork in one click.
          </div>
        </div>
        <div style={{ padding: "0.4rem 1.3rem 1.1rem", display: "grid", gap: "0.5rem" }}>
          <div style={itemRow}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.92rem" }}>Business Structure & Operating Profile</div>
              <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginTop: 2 }}>
                v2026.06.01 · LLC structure, NAICS, ownership, scope, TREC firewall, banking posture. The one you forward to banks.
              </div>
            </div>
            <a href={`${BACKEND_URL}/api/admin/business-structure/pdf`} target="_blank" rel="noreferrer"
               data-testid="biz-structure-pdf"
               style={{ ...ctaBtn("#B8915A", "#1b2a4e"), textDecoration: "none", display: "inline-block" }}>
              📄 Download PDF
            </a>
            <button type="button" data-testid="biz-structure-copy"
                    onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND_URL}/api/admin/business-structure/text`, { credentials: "include" });
                        const d = await r.json();
                        await navigator.clipboard.writeText(d.markdown || "");
                        window.alert("✓ Business Structure copied — paste anywhere (Notes, email, grant app).");
                      } catch (e) {
                        console.warn("[Cockpit] biz-structure copy failed:", e);
                        window.alert("Could not copy. Use the PDF download instead.");
                      }
                    }}
                    style={ctaBtn("transparent", "#B8915A", "#B8915A")}>
              📋 Copy to Clipboard
            </button>
          </div>
          <div style={itemRow}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.92rem" }}>Tier A Master Contractor Agreement</div>
              <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginTop: 2 }}>
                v2026.07.01 · 11-section Cushman-style ICA for your direct crew (cleaning · punch-out · cosmetic). 50/50 milestone posture · TREC firewall · COI required.
              </div>
            </div>
            <a href={`${BACKEND_URL}/api/admin/tier-a-agreement/pdf`} target="_blank" rel="noreferrer"
               data-testid="tier-a-agreement-pdf"
               style={{ ...ctaBtn("#B8915A", "#1b2a4e"), textDecoration: "none", display: "inline-block" }}>
              📄 Download PDF
            </a>
            <button type="button" data-testid="tier-a-agreement-copy"
                    onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND_URL}/api/admin/tier-a-agreement/text`, { credentials: "include" });
                        const d = await r.json();
                        await navigator.clipboard.writeText(d.body || "");
                        window.alert("✓ Tier A Agreement copied — paste anywhere.");
                      } catch (e) {
                        console.warn("[Cockpit] tier-a copy failed:", e);
                        window.alert("Could not copy. Use the PDF download instead.");
                      }
                    }}
                    style={ctaBtn("transparent", "#B8915A", "#B8915A")}>
              📋 Copy to Clipboard
            </button>
          </div>
          <div style={itemRow}>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.92rem" }}>Vendor Network Disclaimer</div>
              <div style={{ color: "#ADBED1", fontSize: "0.8rem", marginTop: 2 }}>
                v2026.06.01 · 12-clause legal armor. Forward to any vendor asking &ldquo;is there paperwork?&rdquo;
              </div>
            </div>
            <a href={`${BACKEND_URL}/api/vendor-disclaimer/download`} target="_blank" rel="noreferrer"
               data-testid="vendor-disclaimer-pdf"
               style={{ ...ctaBtn("#B8915A", "#1b2a4e"), textDecoration: "none", display: "inline-block" }}>
              📄 Download PDF
            </a>
            <a href="/vendor-disclaimer" target="_blank" rel="noreferrer"
               style={{ ...ctaBtn("transparent", "#B8915A", "#B8915A"), textDecoration: "none", display: "inline-block" }}>
              🔗 Public Link
            </a>
          </div>
        </div>
      </div>
      )}

      {/* Module Connection Map — opens as modal from Nervous System strip */}
      <ModuleFlowModal open={flowOpen} onClose={() => setFlowOpen(false)} setTab={setTab} />
      <InvoiceOwnerModal open={invoiceOwnerOpen} onClose={() => setInvoiceOwnerOpen(false)} />
      <SendProposalModal open={sendProposalOpen} onClose={() => setSendProposalOpen(false)} />
      <BankTrackerModal open={bankTrackerOpen} onClose={() => setBankTrackerOpen(false)} />
      <DoctrineToolsModal open={doctrineOpen} onClose={() => setDoctrineOpen(false)} />
      <VendorHunterModal open={vendorHunterOpen} onClose={() => setVendorHunterOpen(false)} />
      <CountyRegistryModal open={countyRegistryOpen} onClose={() => setCountyRegistryOpen(false)} />
      <MediaLicenseModal open={mediaLicenseOpen} onClose={() => setMediaLicenseOpen(false)} />
      <LiveStatusHealthModal open={liveStatusOpen} onClose={() => setLiveStatusOpen(false)} />
      <PhoneDialerModal open={phoneDialerOpen} onClose={() => setPhoneDialerOpen(false)} />
      <SendSMSModal open={smsOpen} onClose={() => setSmsOpen(false)} />
      <WaterFiltrationModal open={waterOpen} onClose={() => setWaterOpen(false)} />
      <PropertyPhotoRender open={photoRenderOpen} onClose={() => setPhotoRenderOpen(false)} />

      {/* Persistent "?" help panel — Shannon: "I always need directions so
          I can master my dashboard." Also opens the render modal, field
          mode, and FWA QR from a single place. */}
      <CockpitHelpPanel
        onOpenRender={() => setPhotoRenderOpen(true)}
        onOpenFieldMode={() => { window.location.assign("/field-mode"); }}
        onOpenFWAQR={() => { window.location.assign("/fwa-qr"); }}
      />
    </div>
  );
};

// ===== Reusable bits =====
const panelOuter = {
  background: "rgba(15,23,42,0.55)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 10,
};

const panelBadge = (color) => ({
  fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase",
  color, fontWeight: 800,
});

const itemRow = {
  display: "flex", gap: "0.8rem", alignItems: "center",
  background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)",
  borderRadius: 6, padding: "0.7rem 0.9rem",
};

const ctaBtn = (bg, fg, border) => ({
  background: bg, color: fg, border: `1px solid ${border || bg}`,
  padding: "0.45rem 0.85rem", borderRadius: 4,
  fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
  whiteSpace: "nowrap",
});

const Panel = ({ testId, emoji, title, subtitle, accent, items, renderItem, empty, emptyMsg }) => (
  <div data-testid={testId} style={panelOuter}>
    <div style={{ padding: "1.1rem 1.3rem 0.6rem", borderLeft: `4px solid ${accent}` }}>
      <div style={panelBadge(accent)}>{emoji} {title}</div>
      <div style={{ color: "#ADBED1", fontSize: "0.85rem", marginTop: 4 }}>{subtitle}</div>
    </div>
    <div style={{ padding: "0.4rem 1.3rem 1.1rem", display: "grid", gap: "0.5rem" }}>
      {empty ? (
        <div style={{ color: "#9CA3AF", fontSize: "0.86rem", padding: "0.8rem 0.5rem", fontStyle: "italic" }}>
          {emptyMsg}
        </div>
      ) : (
        items.map((it, idx) => renderItem(it, idx))
      )}
    </div>
  </div>
);

export default Cockpit;
